import { companyPreviewPanelStore } from "@/stores/company-preview-panel-store";
import { CitationSourcesProvider } from "@/contexts/citation-sources-provider";
import { SavedProposedTacticsProvider } from "@/contexts/saved-proposed-tactics-provider";
import { convertUIMessagePartsToText } from "@/lib/ai";
import { cn } from "@/lib/utils";
import { ClarificationSkip, ConversationMessageMetadata } from "@shaarp/common";
import { ChatStatus, UIMessage } from "ai";
import { Loader2Icon } from "lucide-react";
import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "@/hooks/useTranslation";
import {
  CHAT_QUICK_REPLIES_GAP_CLASS,
  CHAT_QUICK_REPLIES_STANDALONE_GAP_CLASS,
  CHAT_STACK_GAP_CLASS,
} from "./chat-dialog-primitives";
import { ChatClarificationCard } from "./chat-clarification-card";
import {
  buildThreadRenderItems,
  findWorkContinuation,
  followsClarificationReply,
} from "./chat-clarification-run";
import { ChatMessage } from "./chat-message";
import { ChatQuickReplies } from "./chat-quick-replies";

export function ChatMessages({
  messages,
  status,
  onQuickReplyClick,
  onEditMessage,
  onRetryMessage,
  isCompactingContext = false,
  isResuming = false,
  onCompactConversation,
  isCompacting = false,
  onResumeMessage,
  hasOlderMessages = false,
  isLoadingOlder = false,
  onLoadOlder,
  highlightedMessageId = null,
}: {
  messages: UIMessage<ConversationMessageMetadata>[];
  status: ChatStatus;
  onQuickReplyClick?: (reply: string, skipped?: ClarificationSkip) => void;
  onEditMessage?: (
    message: UIMessage<ConversationMessageMetadata>,
    newText: string
  ) => void;
  onRetryMessage?: () => void;
  isCompactingContext?: boolean;
  /** Whether this turn is a continuation of the bubble already on screen. */
  isResuming?: boolean;
  onCompactConversation?: () => void;
  isCompacting?: boolean;
  /** Asks the assistant to pick up an interrupted answer where it stopped. */
  onResumeMessage?: () => void;
  hasOlderMessages?: boolean;
  isLoadingOlder?: boolean;
  onLoadOlder?: () => void;
  /** Message to briefly flash after a search jump (data-message-id anchor). */
  highlightedMessageId?: string | null;
}) {
  const { t } = useTranslation();
  const topSentinelRef = useRef<HTMLDivElement>(null);
  const mentionsRef = useRef<HTMLDivElement>(null);

  // Keep the conversation in sync with the company side panel: whenever it shows
  // a different company (opening a mention, or stepping through with the
  // chevrons), bring the matching chip into view. Scoped to chat-opened
  // (non-modal) panels so table-opened ones never scroll the chat.
  //
  // The panel anchors each entry to the ordinal of the exact chip it was opened
  // from, so a repeated company scrolls to the occurrence the user clicked — not
  // its first mention. An entry without an anchor (a chip outside the
  // conversation) scrolls nothing.
  //
  // `block: "nearest"` does the right thing on its own: a chip already in view
  // stays put, one below the fold aligns to the bottom (above the input, thanks
  // to the scroll container's scroll-padding-bottom) and one above aligns to the
  // top.
  useEffect(() => {
    return companyPreviewPanelStore.subscribe(() => {
      const state = companyPreviewPanelStore.getSnapshot();
      const container = mentionsRef.current;
      if (!state || state.modal || !container) return;

      const ordinal = state.entries[state.index]?.anchor;
      if (ordinal == null) return;

      // Anchors are ordinals among every mention chip, so the scroll enumerates
      // the same `[data-company-key]` set the panel indexed when it was opened.
      const chip =
        container.querySelectorAll<HTMLElement>("[data-company-key]")[ordinal];
      if (!chip) return;

      chip.scrollIntoView({ behavior: "smooth", block: "nearest" });
      // The flash itself is driven declaratively by the chip (CompanyMention)
      // off the same store, so it survives the chip's re-renders.
    });
  }, []);

  // IntersectionObserver for loading older messages
  useEffect(() => {
    const sentinel = topSentinelRef.current;
    if (!sentinel || !onLoadOlder) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasOlderMessages && !isLoadingOlder) {
          onLoadOlder();
        }
      },
      { rootMargin: "200px" }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasOlderMessages, isLoadingOlder, onLoadOlder]);

  // The last message a continuation may be offered on, which is not always the
  // last message rendered: a failed turn appends an error card at the end of
  // the thread, and reading the tail literally then withdraws the resume
  // buttons from the unfinished answer just above it — the one case where the
  // reader most needs them. The error card is transient and carries no answer,
  // so it is looked through rather than counted.
  const resumableTailIndex = useMemo(() => {
    for (let index = messages.length - 1; index >= 0; index--) {
      if (!messages[index].metadata?.error) return index;
    }

    return -1;
  }, [messages]);

  // When the question each answer replies to left the composer, so a message can
  // time its own turn without being handed the whole thread. Compaction
  // summaries are persisted as user messages, so the question is the last
  // message actually written by the user.
  const turnStartedAtMs = useMemo(() => {
    const startedAt: (number | undefined)[] = [];
    let askedAtMs: number | undefined;
    for (const message of messages) {
      startedAt.push(askedAtMs);
      if (message.role === "user" && message.metadata?.type !== "COMPACTION") {
        const createdAt = message.metadata?.createdAt;
        askedAtMs = createdAt ? new Date(createdAt).getTime() : undefined;
      }
    }
    return { perMessage: startedAt, latest: askedAtMs };
  }, [messages]);

  // A run of clarifying questions and the replies they got renders as one card,
  // so the thread maps what it shows rather than the messages themselves.
  // Derived here rather than in the message itself, which is memoized on
  // primitives and never sees the whole list.
  const renderItems = useMemo(
    () => buildThreadRenderItems(messages),
    [messages]
  );

  // A turn that worked, then asked, is carried on by the answer to the reply:
  // its card shows the work of both, at the top, and the answer's own card
  // stands down. Null while that answer is awaited.
  const isAnswerAwaited = status === "submitted" && !isResuming;
  const workContinuations = useMemo(() => {
    const continuedBy = new Map<number, number | null>();
    const shownAbove = new Set<number>();
    for (const item of renderItems) {
      if (item.kind !== "message" || !item.questionReply) continue;
      const continuation = findWorkContinuation(
        messages,
        item.index,
        isAnswerAwaited
      );
      if (!continuation) continue;
      continuedBy.set(item.index, continuation.index);
      if (continuation.index !== null) shownAbove.add(continuation.index);
    }
    return { continuedBy, shownAbove };
  }, [renderItems, messages, isAnswerAwaited]);

  // Get the last message to check for quick replies
  const lastMessage = messages[messages.length - 1];
  const lastMessageText = useMemo(() => {
    if (!lastMessage || lastMessage.role !== "assistant") return "";
    return convertUIMessagePartsToText(lastMessage);
  }, [lastMessage]);

  // Only show quick replies when:
  // - Not streaming
  // - Last message is from assistant
  // - Message ends with a question
  const showQuickReplies =
    status === "ready" &&
    lastMessage?.role === "assistant" &&
    !lastMessage.metadata?.error &&
    onQuickReplyClick;

  // The replies ride along inside the last message whenever the thread ends on
  // one, so the message's hover-only meta footer — overlaid into the gap right
  // under it — is pushed below them instead of showing through the chips.
  // A thread ending on a clarification run has no such message: the card is
  // built from several of them, and the replies stay a sibling in the stack.
  const lastRenderItem = renderItems[renderItems.length - 1];
  const canNestQuickReplies =
    lastRenderItem?.kind === "message" &&
    lastRenderItem.index === messages.length - 1;

  const quickReplies = showQuickReplies ? (
    <ChatQuickReplies
      messageText={lastMessageText}
      onQuickReplyClick={onQuickReplyClick}
      disabled={status !== "ready"}
      className={
        canNestQuickReplies
          ? CHAT_QUICK_REPLIES_GAP_CLASS
          : CHAT_QUICK_REPLIES_STANDALONE_GAP_CLASS
      }
    />
  ) : null;

  return (
    <SavedProposedTacticsProvider messages={messages}>
      <CitationSourcesProvider messages={messages}>
        <div
          ref={mentionsRef}
          data-company-mentions-root
          // Deeper at the bottom than at the top: the last turn's meta footer
          // is overlaid below it and takes no height of its own, so without
          // that reserve it prints flush against the composer.
          className={cn("flex flex-col px-3 pt-6 pb-12", CHAT_STACK_GAP_CLASS)}
        >
          {/* Top sentinel for infinite scroll up */}
          {hasOlderMessages && <div ref={topSentinelRef} className="h-1" />}
          {isLoadingOlder && (
            <div className="flex justify-center py-2">
              <Loader2Icon className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          )}

          {renderItems.map((item) => {
            if (item.kind === "clarificationRun") {
              return (
                <div
                  key={item.key}
                  data-message-id={item.messageIds[0]}
                  className={cn(
                    "rounded-xl transition-colors duration-500",
                    highlightedMessageId !== null &&
                      item.messageIds.includes(highlightedMessageId) &&
                      "bg-majorelle/10 ring-1 ring-majorelle/40"
                  )}
                >
                  {/* A search jump pages history back until the id it was given
                      is in the DOM, so every message the run swallowed keeps an
                      anchor — without one, the walk-back runs to its cap. */}
                  {item.messageIds.slice(1).map((messageId) => (
                    <span
                      key={messageId}
                      data-message-id={messageId}
                      className="block h-0"
                      aria-hidden
                    />
                  ))}
                  <ChatClarificationCard
                    steps={item.steps}
                    isTail={item.isTail}
                    disabled={status !== "ready"}
                    onAnswer={onQuickReplyClick}
                  />
                </div>
              );
            }

            // The turn state is read off the position in the thread, not the one
            // in this list: a run swallows messages, so the two drift apart.
            const { message, index, questionReply, absorbedMessageIds } = item;
            const renderedIds = [message.id, ...(absorbedMessageIds ?? [])];
            const continuationIndex = workContinuations.continuedBy.get(index);
            return (
              <div
                key={message.id || index}
                data-message-id={message.id}
                className={cn(
                  "rounded-xl transition-colors duration-500",
                  highlightedMessageId !== null &&
                    renderedIds.includes(highlightedMessageId) &&
                    "bg-majorelle/10 ring-1 ring-majorelle/40"
                )}
              >
                {/* Same reason as a run's anchors: a reply shown inside the card
                    keeps a node of its own, so a search jump paging history back
                    for its id finds it. */}
                {(absorbedMessageIds ?? []).map((messageId) => (
                  <span
                    key={messageId}
                    data-message-id={messageId}
                    className="block h-0"
                    aria-hidden
                  />
                ))}
                <ChatMessage
                  message={message}
                  isLastMessage={index === messages.length - 1}
                  turnStartedAtMs={turnStartedAtMs.perMessage[index]}
                  isResumableTail={index === resumableTailIndex}
                  status={status}
                  onEditMessage={onEditMessage}
                  onRetryMessage={onRetryMessage}
                  onCompactConversation={onCompactConversation}
                  isCompacting={isCompacting}
                  onResumeMessage={onResumeMessage}
                  onAnswerQuestion={onQuickReplyClick}
                  questionReply={questionReply}
                  answersClarification={followsClarificationReply(
                    messages,
                    index - 1
                  )}
                  isWorkContinued={continuationIndex !== undefined}
                  continuedBy={
                    continuationIndex != null
                      ? messages[continuationIndex]
                      : undefined
                  }
                  isContinuationLive={
                    continuationIndex === null ||
                    (status === "streaming" &&
                      continuationIndex === messages.length - 1)
                  }
                  // Undefined off a continuation: the latest start changes on
                  // every question sent, and would re-render every message.
                  continuationStartedAtMs={
                    continuationIndex === undefined
                      ? undefined
                      : continuationIndex === null
                        ? turnStartedAtMs.latest
                        : turnStartedAtMs.perMessage[continuationIndex]
                  }
                  isWorkShownAbove={workContinuations.shownAbove.has(index)}
                  streamStatusLabel={
                    isResuming && isCompactingContext
                      ? t("chat.conversation.compacting")
                      : // A continuation has no placeholder bubble: until its
                        // first chunk, this is the only word on its wait.
                        isResuming && status === "submitted"
                        ? t("chat.message.wait.resuming")
                        : undefined
                  }
                  streamInProgress={
                    (status === "streaming" ||
                      // A continuation waiting on its first chunk is already this
                      // message's turn: the indicator belongs inside it, and the
                      // notice offering to resume must go before a second click
                      // can start the same answer twice.
                      (isResuming && status === "submitted")) &&
                    // The same tail `isResumableTail` uses, and for the same
                    // reason: after a continuation failed, the literal last
                    // message is the error card, which renders nothing of its
                    // own. Keying the indicator there left a retry with no sign
                    // it had started.
                    index === resumableTailIndex
                  }
                  trailer={
                    canNestQuickReplies && item === lastRenderItem
                      ? quickReplies
                      : undefined
                  }
                />
              </div>
            );
          })}
          {/* Never over a continuation: the answer is being written into the
              bubble above, and a placeholder here would show a second assistant
              starting a second answer — the split this whole path exists to
              avoid. That wait is carried by the streaming indicator inside the
              message being continued. */}
          {/* Nor under a card already working on that answer, at the top of
              the turn it carries on. */}
          {isAnswerAwaited &&
            ![...workContinuations.continuedBy.values()].includes(null) && (
            <ChatMessage
              key="submitted"
              isLastMessage
              message={{
                id: "submitted",
                role: "assistant",
                parts: [
                  {
                    type: "tool-thinking",
                    toolCallId: "thinking",
                    state: "input-streaming",
                    // Name the actual wait: a Tier 2 compaction is a blocking
                    // summary call, and calling it "thinking" is what made those
                    // seconds look like the assistant had stalled.
                    input: isCompactingContext
                      ? t("chat.conversation.compacting")
                      : t("chat.conversation.thinking"),
                  },
                ],
              }}
              status="submitted"
              streamInProgress={false}
              isPendingAnswer
              turnStartedAtMs={turnStartedAtMs.latest}
              answersClarification={followsClarificationReply(
                messages,
                messages.length - 1
              )}
            />
          )}
          {!canNestQuickReplies && quickReplies}
        </div>
      </CitationSourcesProvider>
    </SavedProposedTacticsProvider>
  );
}
